import type {
  PblOcCaseFilter,
  PblOcCaseReviewChecklist,
  PblOcCaseReviewForm,
  PblOcManagedCase,
  PblOcPendingReviewCase,
} from './types';
import PATH from '@/router/path';
import { filterPblCaseListRows } from '@/components/Pbl/CaseList/caseListState';
import { pblConsultationMethods, pblLegalServiceNeeds, pblServiceAreas } from '@/data/pbl';
import type { PblCase, PblCaseAssignment, PblCaseReviewPayload, PblCaseStatus, PblMatchingResult } from '@/types/pbl';
import { formatPblEnglishChineseName } from '@/utils/pblDisplay';

const formatFormDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const serviceAreaLabels = Object.fromEntries(pblServiceAreas.map((item) => [item.value, item.defaultLabel]));
const legalNeedLabels = Object.fromEntries(pblLegalServiceNeeds.map((item) => [item.value, item.defaultLabel]));
const consultationMethodLabels = Object.fromEntries(
  pblConsultationMethods.map((item) => [item.value, item.defaultLabel])
);

export const initialOcCaseReviewForm: PblOcCaseReviewForm = {
  checklist: {
    sub_account_eform_completed: '',
    identity_verified: '',
    service_scope_confirmed: '',
    scheme_rules_explained: '',
    online_enquiry_ready: '',
    declarations_completed: '',
  },
  additionalRemarks: '',
  staffSignature: '',
  staffSignaturePreview: '',
  staffSignatureFileId: undefined,
  staffSignedAt: '',
};

export const areAllChecklistItemsYes = (checklist: PblOcCaseReviewChecklist) =>
  Object.values(checklist).every((answer) => answer === 'yes');

export const hasAnyChecklistItemNo = (checklist: PblOcCaseReviewChecklist) =>
  Object.values(checklist).some((answer) => answer === 'no');

export const hasUnansweredChecklistItem = (checklist: PblOcCaseReviewChecklist) =>
  Object.values(checklist).some((answer) => answer === '');

export const signOcCaseReview = (
  form: PblOcCaseReviewForm,
  assistingStaffName: string,
  signature: { dataUrl?: string; fileId?: number; signedAt?: Date } = {}
): PblOcCaseReviewForm => ({
  ...form,
  staffSignature: assistingStaffName.trim() || 'OC Staff',
  staffSignaturePreview: signature.dataUrl ?? form.staffSignaturePreview,
  staffSignatureFileId: signature.fileId ?? form.staffSignatureFileId,
  staffSignedAt: formatFormDate(signature.signedAt ?? new Date()),
});

export const buildOcCaseReviewPayload = (form: PblOcCaseReviewForm): PblCaseReviewPayload => ({
  sub_account_eform_completed: form.checklist.sub_account_eform_completed === 'yes',
  identity_verified: form.checklist.identity_verified === 'yes',
  service_scope_confirmed: form.checklist.service_scope_confirmed === 'yes',
  scheme_rules_explained: form.checklist.scheme_rules_explained === 'yes',
  online_enquiry_ready: form.checklist.online_enquiry_ready === 'yes',
  declarations_completed: form.checklist.declarations_completed === 'yes',
  review_additional_remarks: form.additionalRemarks.trim() || undefined,
  review_staff_signature: form.staffSignature.trim() || undefined,
  review_staff_signature_file_id: form.staffSignatureFileId,
  review_staff_signed_at: form.staffSignedAt,
});

const joinName = (nameEn?: string | null, nameZh?: string | null, fallback?: string | null) =>
  formatPblEnglishChineseName(nameEn, nameZh, fallback);

const labelFromMap = (labels: Record<string, string>, value?: string | null) => (value ? labels[value] || value : '-');

const labelListFromMap = (labels: Record<string, string>, values?: string | string[] | null) => {
  const list = Array.isArray(values) ? values : values ? [values] : [];

  return list.length > 0 ? list.map((value) => labels[value] || value) : [];
};

const getCaseListStatus = (status: PblCaseStatus) => {
  if (status === 'pending_oc_review') return 'to_be_confirmed';
  if (status === 'pending_evaluation') return 'to_be_rated';
  if (status === 'closed') return 'case_closed';
  if (status === 'case_cancelled') return 'case_cancelled';
  return 'in_progress';
};

const caseListStatusLabels: Partial<Record<PblCaseStatus, string>> = {
  pending_oc_review: 'To be confirmed',
  initiated: 'Review ready',
  matching_ready: 'Matching ready',
  ai_matching_in_progress: 'AI matching',
  ai_matched: 'AI matched',
  manual_assignment_required: 'Manual assignment',
  lawyer_invited: 'Invitation sent',
  pending_lawyer_response: 'Awaiting lawyer',
  lawyer_accepted: 'Lawyer accepted',
  lawyer_declined: 'Lawyer declined',
  lawyer_timeout: 'Lawyer timeout',
  pending_reassignment: 'Cases Review',
  oc_checking: 'OC review',
  pending_lawyer_timeslot: 'Awaiting lawyer timeslot',
  pending_appointment_confirmation: 'Awaiting appointment',
  meeting_link_pending: 'Meeting link pending',
  meeting_scheduled: 'Meeting scheduled',
  in_progress: 'In progress',
  pending_documents: 'Pending documents',
  pending_completion: 'Pending completion',
  pending_evaluation: 'To be rated',
  closed: 'Case closed',
  appointment_cancelled: 'Appointment cancelled',
  case_cancelled: 'Case cancelled',
};

const getCaseListStatusLabel = (status: PblCaseStatus) => caseListStatusLabels[status] || 'In progress';

const getCaseEnquirerSignatureImage = (item: PblCase) => {
  const previewUrl = item.case_enquirer_signature_url?.trim();
  if (previewUrl) return previewUrl;
  const relationPath = item.case_enquirer_signature_file?.path?.trim();
  if (relationPath) return relationPath;

  return item.case_enquirer_signature?.trim() || undefined;
};

export const getPblCaseDisplayId = (item: Pick<PblCase, 'case_id' | 'status'>) => {
  const publicCaseId = item.case_id?.trim();

  if (publicCaseId) return publicCaseId;
  if (item.status === 'pending_oc_review') return 'Pending review';

  return '';
};

const assignmentInvitedStatuses = new Set(['invited', 'pending']);
const assignmentDeclinedStatuses = new Set(['declined', 'timeout', 'reassigned', 'removed', 'cancelled']);
const caseReassignmentRequiredStatuses = new Set<PblCaseStatus>([
  'lawyer_declined',
  'lawyer_timeout',
  'pending_reassignment',
]);

const getAssignmentLawyerId = (assignment?: PblCaseAssignment) =>
  assignment?.pbl_lawyer_profile_id || assignment?.lawyer_profile_id || assignment?.lawyer_profile?.id;

const getMatchingResultLawyerId = (result?: PblMatchingResult) =>
  result?.pbl_lawyer_profile_id ||
  result?.lawyer_profile?.id ||
  result?.directory_entry?.pbl_lawyer_profile_id;

const getAssignmentLawyerName = (assignment: PblCaseAssignment) =>
  [assignment.lawyer_profile?.full_name_en, assignment.lawyer_profile?.full_name_zh].filter(Boolean).join(' / ') ||
  assignment.lawyer_profile?.full_name_en ||
  '-';

const getAssignmentDisplayDate = (assignment: PblCaseAssignment) =>
  assignment.responded_at_display ||
  assignment.responded_at ||
  assignment.updated_at_display ||
  assignment.updated_at ||
  assignment.created_at_display ||
  assignment.created_at ||
  '';

const getCaseMatchingState = (item: PblCase) => {
  const assignments = item.assignments || [];
  const firstMatchingResult = item.matching_results?.[0];
  const activeAssignment = assignments.find((assignment) => !assignmentDeclinedStatuses.has(assignment.status));
  const invitedAssignment = assignments.find((assignment) => assignmentInvitedStatuses.has(assignment.status));
  const declinedAssignments = assignments.filter((assignment) => assignmentDeclinedStatuses.has(assignment.status));
  const selectedLawyerId = getAssignmentLawyerId(activeAssignment);
  const aiRecommendedLawyerId = getMatchingResultLawyerId(firstMatchingResult);
  const declinedLawyers =
    declinedAssignments.length > 0
      ? declinedAssignments.map((assignment) => ({
          lawyerName: getAssignmentLawyerName(assignment),
          reason:
            assignment.status === 'reassigned' || assignment.status === 'removed'
              ? assignment.cancel_reason || 'Removed'
              : assignment.decline_reason || 'Declined',
          date: getAssignmentDisplayDate(assignment),
          status: assignment.status,
        }))
      : undefined;

  if (item.status === 'lawyer_invited' || invitedAssignment) {
    const invitedLawyerId = getAssignmentLawyerId(invitedAssignment || activeAssignment);
    return {
      matchingStatus: 'invitation_sent' as const,
      selectedLawyerId: invitedLawyerId,
      aiRecommendedLawyerId: aiRecommendedLawyerId || invitedLawyerId,
      declinedLawyers,
    };
  }

  if (
    caseReassignmentRequiredStatuses.has(item.status) ||
    (item.status === 'manual_assignment_required' && declinedLawyers)
  ) {
    return {
      matchingStatus: 'declined' as const,
      selectedLawyerId,
      aiRecommendedLawyerId,
      declinedLawyers,
    };
  }

  if (item.status === 'manual_assignment_required' && selectedLawyerId) {
    return {
      matchingStatus: 'human_selected' as const,
      selectedLawyerId,
      aiRecommendedLawyerId,
      declinedLawyers,
    };
  }

  if (aiRecommendedLawyerId) {
    return {
      matchingStatus: 'ai_recommended' as const,
      aiRecommendedLawyerId,
      declinedLawyers,
    };
  }

  return {
    declinedLawyers,
  };
};

export const mapPblCaseToOcManagedCase = (item: PblCase): PblOcManagedCase => {
  const primaryLegalServiceNeeds = labelListFromMap(legalNeedLabels, item.primary_legal_service_needs);
  if (primaryLegalServiceNeeds.length === 0 && item.primary_legal_service_need) {
    primaryLegalServiceNeeds.push(labelFromMap(legalNeedLabels, item.primary_legal_service_need));
  }

  const preferredConsultationMethods = labelListFromMap(consultationMethodLabels, item.preferred_consultation_method);
  if (preferredConsultationMethods.length === 0 && item.consultation_method) {
    preferredConsultationMethods.push(labelFromMap(consultationMethodLabels, item.consultation_method));
  }
  const applicantName = joinName(item.citizen_full_name_en, item.citizen_full_name_zh, item.citizen_full_name);

  return {
    id: item.id,
    citizenProfileId: item.pbl_citizen_profile_id,
    backendStatus: item.status,
    caseId: getPblCaseDisplayId(item),
    applicantName,
    legalServiceNeeds: primaryLegalServiceNeeds.join(', ') || '-',
    creationDate: item.created_at_display || item.created_at || '',
    status: getCaseListStatus(item.status),
    statusLabel: getCaseListStatusLabel(item.status),
    officeName: item.oc_office_name || '-',
    assistingStaffName: item.assisting_staff_name || '-',
    officeContact: item.office_contact || '-',
    enquirer: {
      fullName: applicantName,
      identityPartial: item.identity_partial || '-',
      phone: item.phone || '-',
      email: item.email || '-',
      residentialAddress: item.residential_address || '-',
    },
    enquiryCategory: labelFromMap(serviceAreaLabels, item.enquiry_category),
    urgency: item.urgency,
    courtDeadline: item.court_deadline,
    factualBackground: item.factual_background || '-',
    specificQueries: item.specific_queries || '-',
    primaryLegalServiceNeeds,
    preferredConsultationMethods,
    supportingDocuments: (item.documents || []).map((document) => ({
      id: document.id,
      title: document.title,
      document_type: document.document_type,
      storage_file: document.storage_file,
    })),
    enquirerSignatureDate: item.case_enquirer_signed_at || '',
    enquirerSignatureImage: getCaseEnquirerSignatureImage(item),
    ...getCaseMatchingState(item),
  };
};

export const mapPblCaseToOcPendingReviewCase = (item: PblCase): PblOcPendingReviewCase => ({
  ...mapPblCaseToOcManagedCase(item),
  status: 'pending',
});

export const filterPblOcCases = (cases: PblOcManagedCase[], filter: PblOcCaseFilter, keyword: string) =>
  filterPblCaseListRows(cases, filter, keyword);

export const getPblOcManageTableCases = (cases: PblOcManagedCase[]) =>
  cases.filter((item) => item.backendStatus !== 'oc_review_failed');

export const getPblOcPendingReviewCount = (cases: PblOcManagedCase[]) =>
  cases.filter((item) => item.status === 'to_be_confirmed').length;

export const getCaseInitiatedRedirectPath = (caseId: string | number) =>
  PATH.pbl.caseDetail.replace(':caseId', String(caseId));
